import { Icon } from './Icons';
import React, { useState, useEffect, useRef } from 'react';
import { loadAllBloodMarkers, loadLatestMetrics } from '../db';

import { apiPost } from '../api';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildMetrics(blood, body, mental, fitness, profile) {
  const metrics = {};
  if (profile.name) metrics.name = profile.name.split(' ')[0];
  if (profile.dob) { const age = Math.floor((Date.now() - new Date(profile.dob)) / (1000*60*60*24*365.25)); metrics.age = age + ' years old'; }
  if (profile.gender) metrics.gender = profile.gender;
  if (profile.jobTitle) metrics.jobTitle = profile.jobTitle;
  if (profile.smokingStatus) metrics.smokingStatus = profile.smokingStatus;
  if (profile.alcoholUnits) metrics.weeklyAlcoholUnits = profile.alcoholUnits;
  if (profile.medicalConditions) metrics.medicalConditions = profile.medicalConditions;
  if (profile.medications) metrics.currentMedications = profile.medications;
  if (profile.familyHistory) metrics.familyHistory = profile.familyHistory;
  if (blood.systolicBP && blood.diastolicBP) metrics.bloodPressure = blood.systolicBP + '/' + blood.diastolicBP + ' mmHg';
  if (blood.fastingGlucose) metrics.fastingGlucose = blood.fastingGlucose + ' mmol/L';
  if (blood.hba1c) metrics.hba1c = blood.hba1c + '%';
  if (blood.totalCholesterol) metrics.totalCholesterol = blood.totalCholesterol + ' mmol/L';
  if (blood.hdl) metrics.hdl = blood.hdl + ' mmol/L';
  if (blood.ldl) metrics.ldl = blood.ldl + ' mmol/L';
  if (blood.triglycerides) metrics.triglycerides = blood.triglycerides + ' mmol/L';
  if (blood.vitaminD) metrics.vitaminD = blood.vitaminD + ' nmol/L';
  if (blood.vitaminB12) metrics.vitaminB12 = blood.vitaminB12 + ' pmol/L';
  if (blood.ferritin) metrics.ferritin = blood.ferritin + ' ug/L';
  if (blood.tsh) metrics.tsh = blood.tsh + ' mU/L';
  if (blood.crp) metrics.crp = blood.crp + ' mg/L';
  if (blood.egfr) metrics.egfr = blood.egfr + ' ml/min';
  if (blood.alt) metrics.alt = blood.alt + ' U/L';
  if (body.height && body.weight) { const bmi = (parseFloat(body.weight)/Math.pow(parseFloat(body.height)/100,2)).toFixed(1); metrics.bmi = bmi + ' kg/m²'; }
  if (body.waist) metrics.waistCircumference = body.waist + ' cm';
  if (mental.stressScore) metrics.stressScore = mental.stressScore + '/100';
  if (mental.burnoutScore) metrics.burnoutScore = mental.burnoutScore + '/100';
  if (mental.sleepHours) metrics.averageSleep = mental.sleepHours + ' hrs/night';
  if (mental.phq9) metrics.phq9 = mental.phq9 + '/27';
  if (fitness.stepsPerDay) metrics.stepsPerDay = fitness.stepsPerDay;
  if (fitness.restingHR) metrics.restingHR = fitness.restingHR + ' bpm';
  if (fitness.activeMinutes) metrics.activeMinutes = fitness.activeMinutes + ' min/day';
  return metrics;
}

const SUGGESTION_GROUPS = [
  { label: 'My results', suggestions: ['What do my blood results mean overall?', 'Which of my markers should I focus on?', 'Am I at risk of anything based on my data?'] },
  { label: 'Lifestyle', suggestions: ['What lifestyle changes would help me most?', 'How does stress affect my health markers?', 'What should I eat to improve my results?'] },
  { label: 'Specific markers', suggestions: ['How can I improve my cholesterol?', 'What does my glucose level mean?', 'Why is Vitamin D important?'] },
];

function RicoAvatar({ size = 32 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontFamily: "'Playfair Display', serif", fontSize: size * 0.4 + 'px', fontWeight: 500, color: 'var(--primary)' }}>R</span>
    </div>
  );
}

export default function AIAdvisor() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [userMetrics, setUserMetrics] = useState(null);
  const [serverOk, setServerOk] = useState(true);
  const [activeSuggGroup, setActiveSuggGroup] = useState(0);
  const [lastFailedMsg, setLastFailedMsg] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const STORAGE_KEY = 'rico_chat_history';


  // Load persisted chat history
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) setMessages(parsed);
      }
    } catch(e) {}
  }, []);

  // Persist chat history on change
  useEffect(() => {
    if (messages.length > 0) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-40))); } catch(e) {}
    }
  }, [messages]);

  useEffect(() => {
    async function loadData() {
      try {
        const [blood, body, mental, fitness, profile] = await Promise.all([
          loadAllBloodMarkers(), loadLatestMetrics('body'), loadLatestMetrics('mental'),
          loadLatestMetrics('fitness'), loadLatestMetrics('profile')
        ]);
        const metrics = buildMetrics(blood || {}, body || {}, mental || {}, fitness || {}, profile || {});

        // Load today's food log
        const { auth: firebaseAuth } = await import('../firebase');
        const { getDoc, doc, collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const user = firebaseAuth.currentUser;
        if (user) {
          try {
            const today = new Date().toISOString().split('T')[0];
            const q = query(collection(db, 'users', user.uid, 'history', 'nutrition', 'entries'), orderBy('recordedAt', 'desc'), limit(20));
            const snap = await getDocs(q);
            const allEntries = snap.docs.map(d => d.data());
            const todayEntries = allEntries.filter(e => e.date === today);
            const recentEntries = todayEntries.length > 0 ? todayEntries : allEntries.slice(0, 5);

            if (recentEntries.length > 0) {
              const totalCals = recentEntries.reduce((s, e) => s + (parseFloat(e.calories) || 0), 0);
              const totalProtein = recentEntries.reduce((s, e) => s + (parseFloat(e.protein) || 0), 0);
              const totalCarbs = recentEntries.reduce((s, e) => s + (parseFloat(e.carbs) || 0), 0);
              const totalFat = recentEntries.reduce((s, e) => s + (parseFloat(e.fat) || 0), 0);
              const label = todayEntries.length > 0 ? 'today' : 'most recent entries';
              metrics.foodLogDate = todayEntries.length > 0 ? 'today' : (recentEntries[0]?.date || 'recent');
              metrics.foodLog = recentEntries.map(e => e.description).join('; ');
              metrics.foodLogCalories = Math.round(totalCals) + ' kcal (' + label + ')';
              metrics.foodLogProtein = Math.round(totalProtein) + 'g protein';
              metrics.foodLogCarbs = Math.round(totalCarbs) + 'g carbs';
              metrics.foodLogFat = Math.round(totalFat) + 'g fat';
              // Keep old field names for backward compat
              metrics.todayFoodLog = metrics.foodLog;
              metrics.todayCalories = metrics.foodLogCalories;
              metrics.todayProtein = metrics.foodLogProtein;
            }
            // Load nutrition targets
            const np = await getDoc(doc(db, 'users', user.uid, 'latest', 'nutritionProfile'));
            if (np.exists()) {
              const npData = np.data();
              metrics.calorieTarget = npData.targetCalories + ' kcal';
              metrics.proteinTarget = npData.proteinTarget + 'g';
              metrics.carbTarget = npData.carbTarget + 'g';
              metrics.fatTarget = npData.fatTarget + 'g';
              metrics.nutritionGoal = npData.primaryGoal?.replace('_', ' ');
              metrics.tdee = npData.tdee + ' kcal';
              if (npData.favouriteMeals) metrics.foodPreferences = npData.favouriteMeals;
              if (npData.dietaryRestrictions) metrics.dietaryRestrictions = npData.dietaryRestrictions;
            }

            // Load recent training sessions
            const trainingProfile = await getDoc(doc(db, 'users', user.uid, 'latest', 'trainingProfile'));
            if (trainingProfile.exists()) {
              const tp = trainingProfile.data();
              metrics.trainingGoal = tp.goal;
              metrics.trainingExperience = tp.experience;
              metrics.trainingDaysPerWeek = tp.daysPerWeek + ' days/week';
              metrics.trainingEquipment = tp.equipment;
              if (tp.injuries) metrics.trainingInjuries = tp.injuries;
              if (tp.workingWeights) {
                const weights = Object.entries(tp.workingWeights).filter(([,v]) => v).map(([k,v]) => k + ': ' + v + 'kg').join(', ');
                if (weights) metrics.currentWorkingWeights = weights;
              }
            }

            // Load recent training sessions (last 5)
            const sessQ = query(collection(db, 'users', user.uid, 'history', 'training', 'entries'), orderBy('recordedAt', 'desc'), limit(5));
            const sessSnap = await getDocs(sessQ);
            if (!sessSnap.empty) {
              const sessions = sessSnap.docs.map(d => d.data());
              metrics.recentSessions = sessions.map(s =>
                s.date + ': ' + s.sessionName + ' (Week ' + (s.week || '?') + ', ' + (s.exercises || []).length + ' exercises)'
              ).join(' | ');
              metrics.totalSessionsLogged = sessions.length + '+ sessions';
            }

            // Load gamification stats
            const gam = await getDoc(doc(db, 'users', user.uid, 'latest', 'gamification'));
            if (gam.exists()) {
              const g = gam.data();
              metrics.trainingXP = g.xp + ' XP';
              metrics.trainingStreak = g.streak + ' day streak';
              metrics.personalBests = g.totalPBs + ' PBs set';
            }
          } catch(e) { console.log('Food log load error:', e.message); }
        }

        setUserMetrics(metrics);
        const firstName = profile?.name?.split(' ')[0] || profile?.firstName || '';
        // Only set welcome message if no saved history
        const saved = localStorage.getItem('rico_chat_history');
        if (!saved || JSON.parse(saved).length === 0) {
          setMessages([{
            role: 'assistant',
            content: "Hi" + (firstName ? ", " + firstName : "") + " — I'm Rico (Real Integrated Clinical Observations), your personal health advisor here at Levare. I have access to your health data, training sessions, nutrition and food log — I'm here to give you practical, evidence-based advice across all areas of your health. What would you like to talk about?"
          }]);
        }
      } catch (e) {
        const saved = localStorage.getItem('rico_chat_history');
        if (!saved || JSON.parse(saved).length === 0) {
          setMessages([{ role: 'assistant', content: "Hi — I'm Rico, your Levare health advisor. I'm having trouble loading your health data right now, but I'm still happy to answer your health questions. What would you like to know?" }]);
        }
      }
      setMetricsLoading(false);
    }
    loadData();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const data = await apiPost('/api/chat', { messages: newMessages.slice(-20).map(m => ({ role: m.role, content: m.content })), userMetrics });
      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
      setServerOk(true);
      setLastFailedMsg(null);
    } catch (e) {
      setServerOk(false);
      setLastFailedMsg(msg);
      setIsTyping(false);
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting right now.", isError: true }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  }

  const hasData = userMetrics && Object.keys(userMetrics).length > 2;
  const showSuggestions = messages.length <= 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 3rem)', maxHeight: 820, maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '.5rem', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <RicoAvatar size={44} />
          <div>
            <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.1rem' }}>Rico</h1>
            <p style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>
              {metricsLoading ? 'Loading your health data…' : hasData ? 'Real Integrated Clinical Observations · data loaded' : 'Real Integrated Clinical Observations'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          {messages.length > 1 && (
            <button onClick={() => {
              localStorage.removeItem('rico_chat_history');
              setMessages([]);
              setLastFailedMsg(null);
            }} style={{ padding: '.4rem .85rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.72rem', cursor: 'pointer' }}>
              Clear chat
            </button>
          )}
          {hasData && (
            <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'var(--success-dim)', border: '1px solid rgba(0,200,150,.2)', fontSize: '.65rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              ✓ Health data loaded
            </div>
          )}
          {!serverOk && (
            <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'var(--warning-dim)', border: '1px solid rgba(240,160,80,.2)', fontSize: '.65rem', fontWeight: 600, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              Connection issue
            </div>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '.85rem', paddingRight: '.25rem' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: '.6rem' }}>
            {msg.role === 'assistant' && <RicoAvatar size={30} />}
            <div style={{
              maxWidth: '78%',
              background: msg.role === 'user' ? 'var(--primary-dim)' : 'var(--bg-card)',
              border: msg.role === 'user' ? '1px solid rgba(0,174,239,0.2)' : '1px solid var(--border2)',
              borderRadius: msg.role === 'user' ? 'var(--radius-lg) 4px var(--radius-lg) var(--radius-lg)' : '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)',
              padding: '.9rem 1.1rem',
              fontSize: '.83rem',
              lineHeight: 1.7,
              color: 'var(--text)',
              fontWeight: 300,
              whiteSpace: 'pre-wrap'
            }}>
              {msg.content}
            </div>
            {msg.role === 'user' && (
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 600, color: 'var(--primary)', flexShrink: 0 }}>
                You
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: '.6rem' }}>
            <RicoAvatar size={30} />
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.2rem', display: 'flex', gap: '.3rem', alignItems: 'center' }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)', opacity: 0.5, animation: `bounce 1.2s ${i*0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        {/* Rico typing indicator */}
        {isTyping && (
          <div style={{ display: 'flex', gap: '.75rem', marginBottom: '.75rem', alignItems: 'flex-start' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '.65rem', fontWeight: 700, color: 'var(--primary)' }}>R</div>
            <div style={{ padding: '.65rem .9rem', background: 'var(--bg-elevated)', borderRadius: '0 12px 12px 12px', border: '1px solid var(--border2)', display: 'flex', gap: 4, alignItems: 'center' }}>
              <style>{`@keyframes ricoTyping { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }`}</style>
              {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--primary)', opacity: 0.7, animation: `ricoTyping 1.1s ${i*0.18}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions */}
      {showSuggestions && (
        <div style={{ flexShrink: 0, marginBottom: '.85rem' }}>
          <div style={{ display: 'flex', gap: '.4rem', marginBottom: '.6rem' }}>
            {SUGGESTION_GROUPS.map((g, i) => (
              <button key={i} onClick={() => setActiveSuggGroup(i)} style={{
                padding: '.3rem .75rem', borderRadius: '20px', border: '1px solid',
                borderColor: activeSuggGroup === i ? 'var(--primary)' : 'var(--border2)',
                background: activeSuggGroup === i ? 'var(--primary-dim)' : 'transparent',
                color: activeSuggGroup === i ? 'var(--primary)' : 'var(--text3)',
                fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)',
                transition: 'all .15s'
              }}>{g.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
            {SUGGESTION_GROUPS[activeSuggGroup].suggestions.map(s => (
              <button key={s} onClick={() => sendMessage(s)} style={{
                padding: '.4rem .85rem', borderRadius: '20px',
                background: 'var(--bg-card)', border: '1px solid var(--border2)',
                color: 'var(--text2)', fontSize: '.75rem', cursor: 'pointer',
                fontFamily: 'var(--font-body)', fontWeight: 300, transition: 'all .15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; }}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ask Rico anything about your health…"
            rows={1}
            style={{ flex: 1, resize: 'none', padding: '.8rem 1rem', borderRadius: 'var(--radius-lg)', lineHeight: 1.5, minHeight: '46px', maxHeight: '120px', overflowY: 'auto', fontFamily: 'var(--font-body)', fontSize: '.85rem' }}
          />
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading} style={{
            padding: '.8rem 1.4rem', borderRadius: 'var(--radius-lg)', border: 'none',
            background: input.trim() && !loading ? 'var(--primary)' : 'var(--bg-elevated)',
            color: input.trim() && !loading ? '#ffffff' : 'var(--text3)',
            fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500,
            cursor: input.trim() && !loading ? 'pointer' : 'default',
            transition: 'all .15s', flexShrink: 0, height: '46px'
          }}>
            Send →
          </button>
        </div>
        <div style={{ marginTop: '.5rem', fontSize: '.65rem', color: 'var(--text3)', fontWeight: 300, textAlign: 'center' }}>
          Rico provides general health information only and is not a substitute for professional medical advice.
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
